import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { HelpOutline } from '@material-ui/icons'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import { useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'

import InputText from '@/components/input-text'
import Loading from '@/components/loading'
import { NestedMenu } from '@/components/nested-menu'
import { useGetAvailableDisplays } from '@/hooks/queries/use-get-available-displays'
import {
  BaseInputItem,
  BaseInputItemProps,
} from '@/pages/diagnosis-view/components/base-input-item'
import { useEquipmentDefinitionForm } from '@/pages/diagnosis-view/contexts/equipment-definition-form'
import { GetAvailableDisplaysResponse } from '@/services/get-available-displays'

import { useStyles } from './styles'

interface InputItemProps extends BaseInputItemProps {
  from: 'layersMap' | 'mergedLayersMap'
}

export function InputItem({ from, name, bgColor, border }: InputItemProps) {
  const classes = useStyles()
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const { data } = useGetAvailableDisplays()
  const { register, watch, control } = useEquipmentDefinitionForm()
  const [selectName, doseName] = useMemo(
    () => [`${from}.${name}.model`, `${from}.${name}.dose`] as const,
    [from, name],
  )

  const [selectValue, doseValue] = watch([selectName, doseName]) as string[]

  const [initialSelectValue, initialDoseValue] = useMemo(
    () => [selectValue || '', doseValue || ''],
    [],
  )
  const isNoDisplay = selectValue === 'no-display'
  const isDeviceSelected = Boolean(
    data?.find((display) => display.model === selectValue && display.isDevice),
  )
  const isDroneSelected = Boolean(
    data?.find((display) => display.model === selectValue && display.isDrone),
  )

  const isRequiredInputDose = !isNoDisplay && isDeviceSelected

  const hasIntegration = Boolean(
    data?.find(
      (display) => display.model === selectValue && display.integration,
    ),
  )

  function formatDeviceName(name: string) {
    const parts = name.split(' - ')
    if (parts.length === 2) {
      return (
        <span>
          {parts[0]}
          {' - '}
          <span className={classes.boldText}>{parts[1]}</span>
        </span>
      )
    }
    return name
  }

  const groupByCategory =
    data &&
    Object.entries(
      data.reduce((acc, item) => {
        if (!acc[item.category]) {
          acc[item.category] = []
        }
        acc[item.category].push(item)
        return acc
      }, {} as Record<string, GetAvailableDisplaysResponse[]>),
    )

  return (
    <>
      <BaseInputItem name={name} bgColor={bgColor} border={border}>
        <FormControl
          variant='filled'
          color='secondary'
          hiddenLabel
          size='small'
        >
          {!selectValue && (
            <InputLabel id='select-equipment-input-label'>
              Selecionar equipamento
            </InputLabel>
          )}
          <Controller
            control={control}
            name={selectName}
            defaultValue={initialSelectValue}
            render={({ field }) => (
              <Select
                role='combobox'
                className={classes.select}
                labelId='select-equipment-input-label'
                IconComponent={
                  !data
                    ? () => (
                        <Loading
                          className={classes.loadingSelect}
                          size='1.8rem'
                          color='secondary'
                        />
                      )
                    : undefined
                }
                required
                disableUnderline
                {...field}
                open={!!isSelectOpen}
                onOpen={() => setIsSelectOpen(true)}
                onClose={() => setIsSelectOpen(false)}
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  getContentAnchorEl: null,
                  PaperProps: {
                    className: classes.menuPaper,
                  },
                  elevation: 2,
                }}
              >
                <MenuItem
                  value='no-display'
                  onClick={() => {
                    setIsSelectOpen(false)
                  }}
                >
                  - Nenhum equipamento selecionado -
                </MenuItem>
                {data &&
                  data.map((item) => (
                    <MenuItem
                      hidden
                      key={item.model}
                      className={classes.hiddenMenuItem}
                      value={item.model}
                    >
                      {item.name}
                    </MenuItem>
                  ))}

                {groupByCategory?.map(([category, displays]) => (
                  <NestedMenu key={category} title={category}>
                    {displays.map((display) => (
                      <MenuItem
                        key={display.model}
                        onClick={() => {
                          setIsSelectOpen(false)
                          field.onChange(display.model)
                        }}
                      >
                        {formatDeviceName(display.name)}
                      </MenuItem>
                    ))}
                  </NestedMenu>
                ))}
              </Select>
            )}
          />
        </FormControl>

        <InputText
          className={classes.inputText}
          id={`${name}-dose-input`}
          label='Vazão para aplicação (L/ha)'
          type='number'
          defaultValue={initialDoseValue}
          required={isRequiredInputDose}
          disabled={!isRequiredInputDose}
          InputProps={{
            endAdornment: (
              <Tooltip
                title='Insira a vazão do tratamento com defensivos ja diluído
                      que deve ser utilizada durante a pulverização, unidade
                      de medida litros por hectare.'
                classes={{
                  tooltip: classes.tooltip,
                }}
              >
                <HelpOutline />
              </Tooltip>
            ),
          }}
          {...register(doseName, {
            disabled: !isRequiredInputDose,
            required: isRequiredInputDose,
            valueAsNumber: true,
            deps: selectName,
          })}
        />

        {isDroneSelected && (
          <Box className={classes.infoDrone}>
            <ReportProblemOutlinedIcon />
            <Typography variant={'subtitle1'}>
              *A adaptação do arquivo não considera possíveis obstáculos na
              área.
            </Typography>
          </Box>
        )}

        {hasIntegration && (
          <Box className={classes.infoIntegration}>
            <Typography variant={'body1'}>
              *Disponível integração com Operations Center
            </Typography>
            <Tooltip
              title='Habilite essa integração em Gerenciar sua conta e envie seus
              mapas de pulverização diretamente para o Operations Center'
              placement='left'
              arrow={true}
              classes={{
                tooltip: classes.tooltip,
              }}
            >
              <InfoOutlinedIcon />
            </Tooltip>
          </Box>
        )}
      </BaseInputItem>
    </>
  )
}
